<template>
	<div class="container">
		<div class="header-title" @click="$router.back()">
			<van-icon name="arrow-left" class="back-icon" />
			上传资金凭证
		</div>

		<!-- 上传图片区域 -->
		<div class="upload-section">
			<div class="upload-area" @click="handleUpload">
				<div class="upload-placeholder" v-if="!formData.voucherImage">
					<img :src="uploadImg" class="icon" alt="上传图标" />
				</div>
				<div class="preview-wrapper" v-else>
					<img :src="formData.voucherImage" class="preview-image" alt="资金凭证" />
					<div class="preview-actions">
						<van-icon name="replay" class="replay-icon" @click.stop="handleUpload" />
						<van-icon name="clear" class="clear-icon" @click.stop="clearImage" />
					</div>
				</div>
				<input type="file" ref="fileInput" accept="image/*" style="display: none" @change="onFileChange">
			</div>
		</div>

		<!-- 上传按钮 -->
		<van-button class="submit-btn" @click="handleSubmit" :loading="loading" loading-text="提交中..."
			:disabled="!formData.voucherImage || !hasAddress">
			上传资金凭证
		</van-button>

		<!-- 温馨提示文本 -->
		<div class="tip-section">
			<div class="tip-title">温馨提示：</div>
			<div class="tip-content">
				请妥善保管好您的资金凭证<br>
				该凭证作为线下取款的重要凭据<br>
				凭证上传完并审核通过后<br>
				即可前往预约银行办理线下取款
			</div>
		</div>

		<div class="content">
			<!-- 邮寄地址标题 -->
			<div class="address-title">邮寄地址</div>

			<div class="input-wrapper">
				<div class="input-group">
					<span class="input-label">
						<span class="required-star">*</span>
						姓&emsp;&emsp;名：
					</span>
					<input 
						v-model="formData.name" 
						type="text" 
						class="input-field" 
						:readonly="!isEditing"
						:class="{ 'editable': isEditing }"
						placeholder="请输入您的姓名"
						:disabled="saving">
				</div>

				<div class="input-group">
					<span class="input-label">
						<span class="required-star">*</span>
						手机号码：
					</span>
					<input 
						v-model="formData.phone" 
						type="text" 
						class="input-field" 
						:readonly="!isEditing"
						:class="{ 'editable': isEditing }"
						placeholder="请输入您的手机号码"
						:disabled="saving">
				</div>

				<div class="input-group">
					<span class="input-label">
						<span class="required-star">*</span>
						所在地区：
					</span>
					<input 
						v-model="formData.areaText" 
						type="text" 
						class="input-field" 
						:readonly="!isEditing"
						:class="{ 'editable': isEditing }"
						placeholder="请选择您所在地区"
						@click="isEditing && (showAreaPopup = true)"
						:disabled="saving">
				</div>

				<div class="input-group">
					<span class="input-label">
						<span class="required-star">*</span>
						详细地址：
					</span>
					<input 
						v-model="formData.detailAddress" 
						type="text" 
						class="input-field" 
						:readonly="!isEditing"
						:class="{ 'editable': isEditing }"
						placeholder="请输入您的详细地址"
						:disabled="saving">
				</div>
				
				<!-- 地区选择弹窗 -->
				<van-popup v-model="showAreaPopup" position="bottom" round :style="{ height: '40%' }">
					<van-area 
						:area-list="areaList" 
						:value="formData.areaCode" 
						@confirm="onAreaConfirm" 
						@cancel="showAreaPopup = false" 
						title="请选择所在地区" />
				</van-popup>
				
				<!-- 修改/保存按钮区域 -->
				<div class="button-section">
					<van-button 
						v-if="!isEditing && hasAddress" 
						class="edit-btn"
						@click="startEditing"
						:disabled="saving">
						修改地址
					</van-button>
					<van-button 
						v-if="!isEditing && !hasAddress" 
						class="edit-btn"
						@click="startEditing"
						:disabled="saving">
						添加地址
					</van-button>
					<div v-if="isEditing" class="edit-buttons">
						<van-button 
							class="save-btn"
							@click="saveAddress"
							:loading="saving"
							loading-text="保存中...">
							保存地址
						</van-button>
						<van-button 
							class="cancel-btn"
							@click="cancelEditing"
							:disabled="saving">
							取消
						</van-button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import uploadImg from '@/assets/icon/claimVouchers/upload.png';
	import Fetch from '@/utils/fetch';
	import {
		Popup,
		Area,
		Button,
		Icon,
		Toast
	} from 'vant';
	import {
		areaList
	} from '@vant/area-data';

	export default {
		name: "UploadVoucher",
		components: {
			[Popup.name]: Popup,
			[Area.name]: Area,
			[Button.name]: Button,
			[Icon.name]: Icon
		},
		data() {
			return {
				uploadImg: uploadImg,
				formData: {
					phone: '',
					name: '',
					areaCode: '', // 地区编码
					areaText: '', // 地区显示文本
					detailAddress: '', // 详细地址
					voucherImage: '', // 资金凭证图片
				},
				originalData: null, // 保存原始数据用于比较
				backupData: null, // 备份数据用于取消编辑
				showAreaPopup: false,
				areaList: areaList,
				hasAddress: false, // 是否有地址记录
				isEditing: false, // 是否处于编辑模式
				loading: false, // 上传凭证加载状态
				saving: false, // 保存地址加载状态
			};
		},
		created() {
			this.$parent.footer('UploadVoucher', false);
		},
		mounted() {
			// 获取已有地址
			this.getAddress();
		},
		methods: {
			// 触发上传
			handleUpload() {
				this.$refs.fileInput.click();
			},

			// 处理文件选择
			onFileChange(event) {
				const file = event.target.files[0];
				if (!file) return;

				// 检查文件类型
				if (!file.type.match('image.*')) {
					Toast('请选择图片文件');
					return;
				}

				// 检查文件大小
				if (file.size > 20 * 1024 * 1024) {
					Toast('图片大小不能超过20MB');
					return;
				}

				const reader = new FileReader();
				reader.onload = (e) => {
					this.formData.voucherImage = e.target.result;
				};
				reader.readAsDataURL(file);

				// 重置input，以便可以重新选择同一文件
				event.target.value = '';
			},

			// 清除图片
			clearImage() {
				this.formData.voucherImage = '';
			},

			// 获取地址
			getAddress() {
				Fetch('/user/voucher_address_get').then((r) => {
					if (r.code === 1 && r.data) {
						// 有地址数据
						this.hasAddress = true;

						// 将后端返回的地址数据赋值给formData
						this.formData = {
							...this.formData,
							...r.data
						};

						// 如果后端返回的地址是一个完整字符串，需要拆分处理
						if (r.data.address) {
							this.splitAddress(r.data.address);
						}

						// 保存原始数据，用于比较是否有修改
						this.originalData = {
							...this.formData
						};
					} else {
						// 没有地址或获取失败
						this.hasAddress = false;
						this.originalData = null;
						// 如果没有地址，自动进入编辑模式
						this.isEditing = true;
					}
				}).catch(() => {
					this.hasAddress = false;
					this.originalData = null;
					this.isEditing = true;
				});
			},

			// 拆分地址字符串为地区地址和详细地址
			splitAddress(fullAddress) {
				if (!fullAddress) return;

				// 按空格分割地址字符串
				const parts = fullAddress.split(' ');

				if (parts.length >= 3) {
					// 前三个部分作为省、市、区
					const province = parts[0] || '';
					const city = parts[1] || '';
					const district = parts[2] || '';

					// 拼接省市区作为地区地址
					this.formData.areaText = [province, city, district].filter(Boolean).join(' ');

					// 剩余部分作为详细地址
					this.formData.detailAddress = parts.slice(3).join(' ');
				} else if (parts.length === 2) {
					// 只有两个部分的情况（如直辖市）
					const province = parts[0] || '';
					const city = parts[1] || '';

					this.formData.areaText = [province, city].filter(Boolean).join(' ');
					this.formData.detailAddress = '';
				} else {
					// 只有一个部分或格式不正确
					this.formData.areaText = fullAddress;
					this.formData.detailAddress = '';
				}
			},

			// 检查是否有字段被修改
			hasDataChanged() {
				if (!this.originalData) {
					// 首次填写，所有字段都需要
					return true;
				}

				// 检查所有字段是否有修改
				return (
					this.formData.name.trim() !== this.originalData.name ||
					this.formData.phone.trim() !== this.originalData.phone ||
					this.formData.areaText.trim() !== this.originalData.areaText ||
					this.formData.detailAddress.trim() !== this.originalData.detailAddress
				);
			},
			
			// 开始编辑
			startEditing() {
				// 备份当前数据
				this.backupData = { ...this.formData };
				this.isEditing = true;
			},
			
			// 取消编辑
			cancelEditing() {
				// 恢复备份的数据
				if (this.backupData) {
					this.formData = { ...this.backupData };
				}
				this.isEditing = false;
				this.showAreaPopup = false;
			},
			
			// 验证地址表单
			validateAddressForm() {
				// 验证必填字段
				if (!this.formData.name || !this.formData.phone || !this.formData.areaText || !this.formData.detailAddress) {
					Toast('请填写完整的收货信息');
					return false;
				}

				// 验证姓名（中文姓名，2-10个汉字）
				const nameRegex = /^[\u4e00-\u9fa5]{2,10}$/;
				if (!nameRegex.test(this.formData.name.trim())) {
					Toast('请输入正确的姓名（2-10个汉字）');
					return false;
				}

				// 验证手机号格式
				const phoneRegex = /^\d{11}$/;
				if (!phoneRegex.test(this.formData.phone.trim())) {
					Toast('请输入正确的手机号码');
					return false;
				}

				return true;
			},
			
			// 保存地址
			saveAddress() {
				if (!this.validateAddressForm()) {
					return false;
				}
				
				// 如果数据没有修改，直接退出编辑模式
				if (this.originalData && !this.hasDataChanged()) {
					Toast('收货信息未修改');
					this.isEditing = false;
					return;
				}

				this.saving = true;
				
				// 构建完整的地址信息
				const fullAddress = {
					name: this.formData.name.trim(),
					phone: this.formData.phone.trim(),
					address: this.formData.areaText.trim() + ' ' + this.formData.detailAddress.trim(), // 合并地区地址和详细地址
				};
				
				Fetch('/user/voucher_address_add', fullAddress).then(res => {
					this.saving = false;
					
					if (res.code === 1) {
						Toast('收货信息保存成功');
						
						// 更新状态和原始数据
						this.hasAddress = true;
						this.originalData = {
							...this.formData
						};
						this.isEditing = false;
					} else {
						Toast(res.info['zh_cn'] ? res.info['zh_cn'] : res.info);
					}
				}).catch(error => {
					this.saving = false;
					console.error('保存地址失败:', error);
					Toast('保存失败，请稍后重试');
				});
			},

			// 验证凭证表单
			validateForm() {
				// 验证资金凭证图片
				if (!this.formData.voucherImage) {
					Toast('请上传资金凭证');
					return false;
				}
				
				// 验证是否有收货地址
				if (!this.hasAddress) {
					Toast('请先填写并保存收货地址');
					return false;
				}

				return true;
			},

			// 提交表单 - 修改为不调用接口，直接显示成功并返回首页
			async handleSubmit() {
				if (!this.validateForm()) {
					return;
				}

				this.loading = true;

				try {
					// 模拟成功上传
					await new Promise(resolve => setTimeout(resolve, 1000));

					Toast({
						message: '上传成功',
						duration: 1500
					});

					// 保存原始数据
					this.originalData = {
						...this.formData
					};

					// 延迟1.5秒后返回首页
					setTimeout(() => {
						this.$router.push('/');
					}, 1500);
				} catch (error) {
					console.error('提交失败:', error);
					Toast('提交失败，请重试');
				} finally {
					this.loading = false;
				}
			},

			// 地区选择确认
			onAreaConfirm(selectedValues) {
				// selectedValues 是选中的地区对象数组
				if (selectedValues && selectedValues.length > 0) {
					// 拼接地区文本
					const areaText = selectedValues.map(area => area.name).join(' ');
					// 获取地区编码（通常是最后一个选中的地区的编码）
					const areaCode = selectedValues[selectedValues.length - 1].code;

					this.formData.areaText = areaText;
					this.formData.areaCode = areaCode;
				}
				this.showAreaPopup = false;
			},
		}
	};
</script>

<style scoped>
	.container {
		display: flex;
		text-align: center;
		align-items: center;
		flex-direction: column;
		font-family: "微软雅黑", sans-serif;
		height: 100vh;
		width: 10rem;
		background: url('../../assets/icon/appointmentFailed/bg.jpg') 100% no-repeat;
		background-size: 100% 100%;
		position: relative;
		padding-bottom: 2rem;
		overflow-y: auto;
	}

	.header-title {
		position: relative;
		z-index: 1;
		width: 100%;
		text-align: center;
		font-size: 4.8vw;
		font-weight: 600;
		padding: 4vw 0;
		background: #fff;
		box-shadow: 0 1.06667vw 3.2vw rgba(0, 0, 0, .05);
		color: #333;
		border-bottom: 1px solid rgba(0, 0, 0, .05);
	}

	.header-title .back-icon {
		position: absolute;
		left: 4vw;
		top: 50%;
		transform: translateY(-50%);
		font-size: 5.33333vw;
		color: #333;
	}

	/* 上传区域样式 */
	.upload-section {
		width: 90%;
		margin: 1rem 0;
	}

	.upload-area {
		width: 100%;
		border-radius: 15px;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
		overflow: hidden;
		cursor: pointer;
		transition: border-color 0.3s;
	}

	.upload-area:hover {
		border-color: #134993;
	}

	.upload-placeholder {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 100%;
		box-sizing: border-box;
	}

	.upload-placeholder .icon {
		width: 100%;
	}

	.upload-text {
		font-size: 4.2vw;
		color: #333;
		font-weight: 500;
		margin-bottom: 0.3rem;
	}

	.upload-tip {
		font-size: 3.5vw;
		color: #999;
		text-align: center;
	}

	.preview-wrapper {
		width: 100%;
		height: 100%;
		position: relative;
	}

	.preview-image {
		width: 100%;
		height: 100%;
		object-fit: contain;
		background-color: #f5f5f5;
	}

	.preview-actions {
		position: absolute;
		bottom: 10px;
		right: 10px;
		display: flex;
		gap: 10px;
	}

	.replay-icon,
	.clear-icon {
		width: 36px;
		height: 36px;
		background-color: rgba(0, 0, 0, 0.5);
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		color: white;
		font-size: 18px;
	}

	/* 上传按钮 */
	.submit-btn {
		width: 80%;
		color: #fff;
		font-size: 22px;
		font-weight: bold;
		border-radius: 25px;
		background-color: #134993;
		padding: 12px;
		border: none;
		cursor: pointer;
		margin-bottom: 1rem;
	}

	.submit-btn:disabled {
		background-color: #ccc;
		color: #666;
		cursor: not-allowed;
	}

	/* 温馨提示区域 */
	.tip-section {
		width: 90%;
		margin: 0.5rem 0 1.5rem 0;
		padding: 15px;
		background-color: rgba(19, 73, 147, 0.05);
		border-radius: 10px;
		color: #134993;
		font-weight: bold;
	}

	.tip-title {
		font-size: 5.5vw;
		text-align: left;
		margin-bottom: 8px;
	}

	.tip-content {
		font-size: 4vw;
		text-align: left;
		line-height: 1.5;
	}

	/* 邮寄地址标题 */
	.address-title {
		width: 100%;
		color: #134993;
		font-size: 5.5vw;
		font-weight: bold;
		text-align: center;
		margin-bottom: 20px;
	}

	.content {
		width: 90%;
		border-radius: 12px;
		padding: 20px;
		margin-bottom: 15px;
		box-sizing: border-box;
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
		backdrop-filter: blur(5px);
		border: 1px solid rgba(255, 255, 255, 0.1);
	}

	.input-wrapper {
		width: 100%;
	}

	.input-group {
		display: flex;
		align-items: center;
		margin-bottom: 15px;
		padding: 10px;
		border-bottom: 1px solid #ebedf0;
		position: relative;
		border-radius: 5px;
		background: rgba(255, 255, 255, 0.95);
		box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
	}

	.input-group:last-child {
		border-bottom: none;
		margin-bottom: 10px;
	}

	.input-label {
		font-size: 14px;
		color: #333;
		font-weight: 500;
		text-align: left;
		position: relative;
		display: flex;
		align-items: center;
		white-space: nowrap;
		width: 100px;
	}

	.required-star {
		color: #ee0a24;
		font-size: 14px;
		margin: 0 2px;
		line-height: 1;
	}

	.input-field {
		flex: 1;
		border: none;
		outline: none;
		font-size: 14px;
		background: transparent;
		color: #333;
		padding: 0;
	}

	.input-field.editable {
		background-color: rgba(255, 255, 255, 0.9);
		cursor: text;
	}

	.input-field[readonly] {
		color: #666;
		cursor: default;
		background-color: rgba(245, 245, 245, 0.7);
	}

	.input-field:disabled {
		background-color: #f8f8f8;
		color: #666;
		cursor: not-allowed;
	}

	.input-field::placeholder {
		color: #969799;
	}
	
	/* 按钮区域样式 */
	.button-section {
		width: 100%;
		margin-top: 20px;
		display: flex;
		flex-direction: column;
		align-items: center;
	}
	
	.edit-btn {
		width: 80%;
		color: #fff;
		font-size: 16px;
		font-weight: bold;
		border-radius: 25px;
		background-color: #134993;
		padding: 10px;
		border: none;
		cursor: pointer;
	}
	
	.edit-buttons {
		width: 100%;
		display: flex;
		justify-content: space-between;
		gap: 15px;
	}
	
	.save-btn {
		flex: 1;
		color: #fff;
		font-size: 16px;
		font-weight: bold;
		border-radius: 25px;
		background-color: #07c160;
		padding: 10px;
		border: none;
		cursor: pointer;
	}
	
	.cancel-btn {
		flex: 1;
		color: #fff;
		font-size: 16px;
		font-weight: bold;
		border-radius: 25px;
		background-color: #ee0a24;
		padding: 10px;
		border: none;
		cursor: pointer;
	}
	
	.edit-btn:disabled,
	.save-btn:disabled,
	.cancel-btn:disabled {
		background-color: #ccc;
		color: #666;
		cursor: not-allowed;
	}
</style>